import { useEffect, useRef } from 'react'

import { FxFieldChip, confidenceTone } from '@/components/fx'
import { cn } from '@/lib/utils'
import {
  isResultFieldReviewed,
  pageByNumber,
  resultFieldFlagReason,
  resultFieldValue,
} from '@/types/domain'
import type {
  BoxConfiguration,
  DocumentResultField,
  EvaluationDocument,
  Position,
  Template,
} from '@/types/domain'

/** The chip's glyph family, which is fixed per field type. */
export type ChipFieldType = 'text' | 'checkbox' | 'signature'

/**
 * One analysed field, joined and ready to render — as a chip in the list and
 * as a rectangle on the page.
 *
 * Both views are built from one entry on purpose: the selection is shared
 * between them, and a list and an overlay computed separately drift apart the
 * first time one of them learns to skip a field.
 */
export interface ResultFieldEntry {
  /** The box id, which is how `EvaluationDocument.result` is keyed. */
  id: string
  name: string
  type: ChipFieldType
  /** The delivered value, through `resultFieldValue` — never `text`. */
  value: string
  confidence: number
  /** Waiting for a person: nothing recognised, below the template's
   *  threshold, or flagged by the analyzer. */
  flagged: boolean
  /** Why, in German, when there is a reason the data supports. */
  flagLabel?: string
  tone: 'success' | 'warning' | 'danger'
  /** The rectangle in the **page image's** own pixels, or null for a field
   *  whose stored result carries no usable box. */
  rect: Position | null
}

const CHIP_TYPE: Record<string, ChipFieldType> = {
  textDetection: 'text',
  checkBox: 'checkbox',
  // A radio group is a row of checkboxes as far as the reader is concerned,
  // and there is no third glyph for it.
  radioBox: 'checkbox',
  signature: 'signature',
}

/** The result's box, or null when it has none to speak of. */
function rectOf(field: DocumentResultField): Position | null {
  const width = field.x2 - field.x
  const height = field.y2 - field.y
  if (!(width > 0) || !(height > 0)) return null
  return { x: field.x, y: field.y, width, height }
}

function entryFor(
  id: string,
  field: DocumentResultField,
  box: BoxConfiguration | undefined,
  threshold: number,
): ResultFieldEntry {
  const reason = resultFieldFlagReason(field, threshold)
  // `needsReview` is the analyzer's own verdict — it knows things the
  // confidence does not, such as ink in a field that read as empty. A
  // reviewed field is never flagged again: somebody has decided.
  const flagged = reason != null || (!isResultFieldReviewed(field) && field.needsReview === true)
  return {
    id,
    // The result map is keyed by box id, so the label only exists on the
    // template. A box that is gone — a page edited after the analysis —
    // leaves the id, which is unlovely but true; inventing a name would be
    // worse.
    name: box?.label ?? id,
    type: CHIP_TYPE[box?.type ?? 'textDetection'] ?? 'text',
    value: resultFieldValue(field),
    confidence: field.confidence ?? 0,
    flagged,
    flagLabel: reason?.label,
    tone: flagged ? 'danger' : confidenceTone(field.confidence ?? 0, threshold),
    rect: rectOf(field),
  }
}

/**
 * Joins a document's stored analysis against the layout it was matched to.
 *
 * Order follows the template's boxes, so the chips run down the page the way
 * the form does rather than in whatever order the result map was serialised
 * in. Results whose box is no longer on the page are appended rather than
 * dropped: they are data somebody paid for.
 */
export function resultFieldEntries(
  doc: EvaluationDocument | undefined,
  template: Template | undefined,
  threshold: number,
): ResultFieldEntry[] {
  const result = doc?.result ?? {}
  const page = template && doc?.matchedPage != null ? pageByNumber(template, doc.matchedPage) : undefined
  const boxes = page?.boxConfiguration ?? []

  const entries: ResultFieldEntry[] = []
  const seen = new Set<string>()
  for (const box of boxes) {
    const field = result[box.id]
    if (!field) continue
    seen.add(box.id)
    entries.push(entryFor(box.id, field, box, threshold))
  }
  for (const [id, field] of Object.entries(result)) {
    if (seen.has(id)) continue
    entries.push(entryFor(id, field, undefined, threshold))
  }
  return entries
}

export interface FieldChipListProps {
  fields: ResultFieldEntry[]
  selectedId?: string | null
  onSelect?: (id: string) => void
  /** Shown in place of the list when `fields` is empty. */
  emptyText?: string
  /** Names the list for a screen reader ("Unklare Felder"). */
  label: string
  className?: string
}

const TONE_TEXT = {
  success: 'text-success-on-subtle',
  warning: 'text-warning-text',
  danger: 'text-danger-text',
} as const

/**
 * The read fields as chips, selectable and keyboard-operable.
 *
 * Each chip is a real button, so the list is one tab stop with the arrow keys
 * moving inside it — the pattern the Flutter review screen bound to
 * `PreviousFieldIntent`/`SkipFieldIntent`, minus the review actions this
 * read-only list has no business offering. The selection is controlled by the
 * caller so the page viewer beside it shares one.
 */
export function FieldChipList({
  fields,
  selectedId,
  onSelect,
  emptyText,
  label,
  className,
}: FieldChipListProps) {
  const buttons = useRef<(HTMLButtonElement | null)[]>([])
  const list = useRef<HTMLUListElement | null>(null)

  // A selection made on the page must become visible in the list, which is
  // scrollable and usually shows a handful of a few dozen chips.
  useEffect(() => {
    if (!selectedId) return
    const index = fields.findIndex((field) => field.id === selectedId)
    // Optional call: jsdom has no scrollIntoView, and a chip filtered out of
    // the list has no node.
    buttons.current[index]?.scrollIntoView?.({ block: 'nearest' })
    // Only a change of selection should scroll; a re-render must not yank the
    // list back while somebody is reading further down.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId])

  if (fields.length === 0) {
    return <p className={cn('text-body text-text-muted', className)}>{emptyText}</p>
  }

  const selectedIndex = fields.findIndex((field) => field.id === selectedId)
  const focusIndex = selectedIndex < 0 ? 0 : selectedIndex

  function move(from: number, event: React.KeyboardEvent) {
    const last = fields.length - 1
    let next: number
    switch (event.key) {
      case 'ArrowDown':
        next = from === last ? 0 : from + 1
        break
      case 'ArrowUp':
        next = from === 0 ? last : from - 1
        break
      case 'Home':
        next = 0
        break
      case 'End':
        next = last
        break
      default:
        return
    }
    event.preventDefault()
    buttons.current[next]?.focus()
    onSelect?.(fields[next].id)
  }

  return (
    <ul ref={list} aria-label={label} className={cn('flex flex-col gap-2', className)}>
      {fields.map((field, index) => (
        <li key={field.id}>
          <button
            ref={(node) => {
              buttons.current[index] = node
            }}
            type="button"
            // Not a toggle: pressing a chip selects it and pressing it again
            // leaves it selected, so `aria-pressed` would describe a control
            // that does not exist. `aria-current` is what "this is the one on
            // the page" means.
            aria-current={field.id === selectedId}
            tabIndex={index === focusIndex ? 0 : -1}
            onKeyDown={(event) => move(index, event)}
            onClick={() => onSelect?.(field.id)}
            className="block w-full text-left"
          >
            <FxFieldChip
              className="w-auto"
              type={field.type}
              name={field.name}
              state={field.flagged ? 'error' : 'result'}
              active={field.id === selectedId}
              value={field.value || undefined}
              trailing={
                <span className={cn('text-small font-semibold tabular-nums', TONE_TEXT[field.tone])}>
                  {Math.round(field.confidence * 100)} %
                </span>
              }
            />
          </button>
        </li>
      ))}
    </ul>
  )
}
